import React, { useState } from 'react'
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Alert,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import EditIcon from '@mui/icons-material/Edit'
import { useMutation } from '@tanstack/react-query'
import { DeveloperService, IUpdateUserRow, IUpdateUsersApiResponseEntry } from '@/api/services/DeveloperService'
import { UpdateUserEntry } from './UpdateUserEntry'
import { primaryButtonSx, outlinedButtonSx, tableHeadCellSx } from '../styles'

const newRow = (): IUpdateUserRow => ({
  rowId: crypto.randomUUID(),
  identifierType: 'userId',
  identifierValue: '',
  enableBankDetails: false,   // unused in table mode — inclusion is inferred from field values
  accountNumber: '',
  bsb: '',
  autoGenerate: false,
  enableRiskPercentile: false, // unused in table mode — inclusion is inferred from field value
  riskPercentile: '',
})

const columnHeaders = [
  { label: 'Identifier', width: 100 },
  { label: 'Value', width: 210 },
  { label: 'Auto-generate', width: 100, align: 'center' as const, tooltip: 'When checked, the backend generates account & BSB' },
  { label: 'Account number', width: 150 },
  { label: 'BSB', width: 100 },
  { label: 'Risk %', width: 90 },
  { label: '', width: 40 },
]

export const UpdateUsers: React.FC = () => {
  const [rows, setRows] = useState<IUpdateUserRow[]>([newRow()])
  const [result, setResult] = useState<IUpdateUsersApiResponseEntry[] | null>(null)

  // ── Mutation ──────────────────────────────────────────────────────────────────
  const updateMutation = useMutation({
    mutationFn: DeveloperService.updateTestUsers,
    onSuccess: (response) => setResult(response.data),
  })

  // ── Handlers ──────────────────────────────────────────────────────────────────
  const handlePatch = (rowId: string, patch: Partial<IUpdateUserRow>) => {
    setRows(prev => prev.map(r => r.rowId === rowId ? { ...r, ...patch } : r))
  }

  const handleRemove = (rowId: string) => {
    setRows(prev => prev.filter(r => r.rowId !== rowId))
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    setResult(null)
    // Infer which optional sections to include from field values
    const enrichedRows = rows
      .filter(r => r.identifierValue.trim())
      .map(r => ({
        ...r,
        enableBankDetails: r.autoGenerate || !!r.accountNumber || !!r.bsb,
        enableRiskPercentile: r.riskPercentile !== '',
      }))
    if (enrichedRows.length === 0) return
    updateMutation.mutate(enrichedRows)
  }

  const isSubmitValid = rows.some(r => r.identifierValue.trim())

  return (
    <Box component="form" onSubmit={handleSubmit} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>

      <Typography variant="body2" color="#a08fa8">
        Update one or more users in a single PATCH request. Leave bank or risk fields empty to skip them.
        Check <strong style={{ color: '#2c063f' }}>Auto-generate</strong> to let the backend assign bank details.
      </Typography>

      {/* Entries table */}
      <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2, borderColor: '#e0d7e4' }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              {columnHeaders.map(col => (
                <TableCell
                  key={col.label}
                  align={col.align ?? 'left'}
                  sx={{ ...tableHeadCellSx, width: col.width, whiteSpace: 'nowrap' }}
                >
                  {col.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map(row => (
              <UpdateUserEntry
                key={row.rowId}
                row={row}
                showRemove={rows.length > 1}
                onPatch={handlePatch}
                onRemove={handleRemove}
              />
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Actions row */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Button
          variant="outlined"
          startIcon={<AddIcon />}
          onClick={() => setRows(prev => [...prev, newRow()])}
          sx={outlinedButtonSx}
        >
          Add row
        </Button>
        <Button
          type="submit" variant="contained"
          disabled={!isSubmitValid || updateMutation.isPending}
          startIcon={updateMutation.isPending ? <CircularProgress size={16} color="inherit" /> : <EditIcon />}
          sx={primaryButtonSx}
        >
          {updateMutation.isPending ? 'Updating…' : `Submit update${rows.length > 1 ? 's' : ''}`}
        </Button>
      </Box>

      {updateMutation.isError && (
        <Alert severity="error">Failed to update users. Check the API logs.</Alert>
      )}

      {/* Response table */}
      {result && result.length > 0 && (
        <Box sx={{ border: '1px solid #00a67c', borderRadius: 2, overflow: 'hidden' }}>
          <Box sx={{ px: 2, py: 1.5, borderBottom: '1px solid #e0d7e4', backgroundColor: '#f5f2f7' }}>
            <Typography variant="subtitle2" sx={{ color: '#00a67c', fontWeight: 700 }}>
              Update successful — {result.length} user{result.length > 1 ? 's' : ''} updated
            </Typography>
          </Box>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  {['User', 'Bank account', 'BSB', 'Auto-generated', 'Risk percentile'].map(h => (
                    <TableCell key={h} sx={tableHeadCellSx}>{h}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {result.map((entry, i) => (
                  <TableRow key={i}>
                    <TableCell>
                      <Typography variant="caption" sx={{ fontFamily: 'monospace', color: '#4b2b5c' }}>
                        {entry.userId ?? entry.email ?? '—'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption" color="#4b2b5c">
                        {entry.bankDetails?.accountNumber || '—'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption" color="#4b2b5c">
                        {entry.bankDetails?.bsb || '—'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {entry.bankDetails != null ? (
                        <Chip
                          label={entry.bankDetails.autoGenerate ? 'Yes' : 'No'}
                          size="small"
                          sx={{
                            fontSize: 11,
                            backgroundColor: entry.bankDetails.autoGenerate ? '#d9f2ec' : '#f5f2f7',
                            color: entry.bankDetails.autoGenerate ? '#00665b' : '#4b2b5c',
                          }}
                        />
                      ) : (
                        <Typography variant="caption" color="#a08fa8">—</Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption" color="#4b2b5c">
                        {entry.riskPercentile != null ? entry.riskPercentile.value : '—'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}

    </Box>
  )
}
